import {
  Component,
  ComponentType,
  ErrorInfo,
  PropsWithChildren,
  ReactNode,
} from 'react';

import {
  hasArrayChanged,
} from '#utils/ArrayChanged';

export type FallbackProps = {
  error: unknown;
  resetErrorBoundary: (...args: unknown[]) => void;
};

type ErrorBoundarySharedProps = PropsWithChildren<{
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: (
    details:
      | { reason: 'imperative-api'; args: unknown[] }
      | { reason: 'keys'; prev: unknown[] | undefined; next: unknown[] | undefined }
  ) => void;
  resetKeys?: unknown[];
}>;

export type ErrorBoundaryPropsWithComponent = ErrorBoundarySharedProps & {
  fallback?: never;
  FallbackComponent: ComponentType<FallbackProps>;
  fallbackRender?: never;
};

export type ErrorBoundaryPropsWithRender = ErrorBoundarySharedProps & {
  fallback?: never;
  FallbackComponent?: never;
  fallbackRender: (props: FallbackProps) => ReactNode;
};

export type ErrorBoundaryPropsWithFallback = ErrorBoundarySharedProps & {
  fallback: ReactNode;
  FallbackComponent?: never;
  fallbackRender?: never;
};

export type ErrorBoundaryProps =
  | ErrorBoundaryPropsWithFallback
  | ErrorBoundaryPropsWithComponent
  | ErrorBoundaryPropsWithRender;

export type ErrorBoundaryState =
  | {
    didCatch: true;
    error: unknown;
  }
  | {
    didCatch: false;
    error: null;
  };

const initialState: ErrorBoundaryState = {
  didCatch: false,
  error: null,
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
    this.state = initialState;
  }

  static getDerivedStateFromError(error: Error) {
    return {
      didCatch: true,
      error,
    };
  }

  resetErrorBoundary(...args: unknown[]) {
    const {
      error,
    } = this.state;
    if (error !== null) {
      this.props.onReset?.({
        args,
        reason: 'imperative-api',
      });
      this.setState(initialState);
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.onError) {
      this.props.onError?.(error, info);
    } else {
      console.error(error, info);
    }
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState,
  ) {
    const {
      didCatch,
    } = this.state;
    const {
      resetKeys,
    } = this.props;
    // There's an edge case where if the thing that triggered the error happens to *also* be in the resetKeys array,
    // we'd end up resetting the error boundary immediately.
    // This would likely trigger a second error to be thrown.
    // So we make sure that we don't check the resetKeys on the first call of cDU after the error is set.

    if (
      didCatch
      && prevState.error !== null
      && hasArrayChanged(
        prevProps.resetKeys,
        resetKeys,
      )
    ) {
      this.props.onReset?.({
        next: resetKeys,
        prev: prevProps.resetKeys,
        reason: 'keys',
      });
      this.setState(initialState);
    }
  }

  render() {
    if (this.state.error) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }
    return this.props.children;
  }
}
